import { useEffect, useMemo, useState } from 'react';
import { testimonialApi } from '../../shared/api/endpoints';
import './Testimonials.css';

function chunkPairs(list) {
  const pairs = [];
  for (let i = 0; i < list.length; i += 2) {
    pairs.push(list.slice(i, i + 2));
  }
  return pairs;
}

function TestimonialCard({ t }) {
  return (
    <article className="love__card">
      <div
        className={`love__card-media${t.image ? '' : ' love__card-media--empty'}`}
        style={t.image ? { backgroundImage: `url(${t.image})` } : undefined}
        role="img"
        aria-label={t.image ? `Piece for ${t.name}` : 'No product image'}
      />
      <div className="love__card-body">
        <p className="love__quote">“{t.message}”</p>
        <div className="love__card-foot">
          <div className="love__who">
            <h3>{t.name}</h3>
            {t.role && <span>{t.role}</span>}
          </div>
          <div className="love__stars" aria-label={`${t.rating || 5} stars`}>
            {'★'.repeat(t.rating || 5)}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    testimonialApi
      .list()
      .then((res) => setItems(res.data.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  const avg = useMemo(() => {
    if (!items.length) return 0;
    const sum = items.reduce((s, t) => s + (t.rating || 5), 0);
    return Math.round((sum / items.length) * 100) / 100;
  }, [items]);

  const bars = useMemo(() => {
    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    items.forEach((t) => {
      const r = Math.min(5, Math.max(1, Math.round(t.rating || 5)));
      counts[r] += 1;
    });
    const total = items.length || 1;
    return [5, 4, 3, 2, 1].map((star) => ({
      star,
      pct: Math.round((counts[star] / total) * 100),
    }));
  }, [items]);

  const slides = useMemo(() => chunkPairs(items), [items]);
  // Duplicate for seamless continuous loop (need at least 2 slides worth)
  const loopSlides = useMemo(() => {
    if (!slides.length) return [];
    if (slides.length === 1) return [...slides, ...slides, ...slides, ...slides];
    return [...slides, ...slides];
  }, [slides]);

  const durationSec = Math.max(18, slides.length * 8);

  if (!loaded || items.length === 0) return null;

  return (
    <section className="section love section--dark">
      <div className="container">
        <div className="love__intro">
          <div>
            <p className="section-eyebrow">Community</p>
            <h2 className="section-title">Why People Love NAYAVARNAM</h2>
            <p className="section-sub">Stories from those who wore the glow.</p>
          </div>
        </div>

        <div className="love__layout">
          <aside className="love__summary">
            <div className="love__score">
              <strong>{avg.toFixed(2)}</strong>
              <span>out of 5</span>
            </div>
            <div className="love__stars" aria-hidden>
              {'★'.repeat(Math.round(avg) || 5)}
            </div>
            <p className="love__count">
              {items.length} review{items.length === 1 ? '' : 's'}
            </p>
            <div className="love__bars">
              {bars.map((b) => (
                <div key={b.star} className="love__bar-row">
                  <span>{b.star}</span>
                  <div className="love__bar-track">
                    <i style={{ width: `${b.pct}%` }} />
                  </div>
                  <em>{b.pct}%</em>
                </div>
              ))}
            </div>
          </aside>

          <div className="love__carousel" aria-label="Customer stories carousel">
            <div
              className="love__track"
              style={{ animationDuration: `${durationSec}s` }}
            >
              {loopSlides.map((pair, slideIdx) => (
                <div className="love__slide" key={`slide-${slideIdx}`}>
                  {pair.map((t) => (
                    <TestimonialCard key={`${slideIdx}-${t._id}`} t={t} />
                  ))}
                  {/* Keep 2-column rhythm when odd last pair */}
                  {pair.length === 1 && <div className="love__card love__card--spacer" aria-hidden />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
